import type { OpsAlert, OpsSeverity } from "../api/types";

/**
 * Карточка операционной тревоги: вся логика показа.
 *
 * Логика живёт здесь, а не в .tsx, потому что компонентных тестов в проекте нет
 * вовсе — vitest собирает только src/**\/*.test.ts. Значит всё, что можно
 * сломать молча, обязано лежать в .ts, а в компоненте остаётся разметка.
 *
 * Подсистема называется opswatch, а не alert, и файл тоже: LockAlerts и
 * SignalAlerts — это ТОРГОВЫЕ карточки (предложение сделки), а здесь поломка
 * стека. Два смысла под одним словом в поиске стоят дороже непривычного имени.
 */

/** Состояния, которыми приезжает событие. Совпадают с State.String() в Go. */
export const STATE_FIRING = "тревога";
export const STATE_RESOLVED = "снято";
export const STATE_MUTED = "заглушено";
export const STATE_BINDING = "потеряна нота";

/** Порядок тяжести: crit выше warn выше info. */
const WEIGHT: Record<OpsSeverity, number> = { crit: 3, warn: 2, info: 1 };

export function severityWeight(s: OpsSeverity | string | undefined): number {
  return WEIGHT[(s ?? "info") as OpsSeverity] ?? 1;
}

/** Живая ли тревога: снятая карточка остаётся на экране, но уже как след. */
export function isLive(a: OpsAlert): boolean {
  return a.state !== STATE_RESOLVED;
}

/**
 * Ключ звонка.
 *
 * Идентификатор ПЛЮС состояние, а не один идентификатор: тревога и её снятие
 * несут общий id (карточка замещается по нему), и звонок по одному id прозвучал
 * бы только на подъёме, а возврат в норму прошёл бы молча. Тот же приём, что у
 * chimeKey в lockcard.ts.
 */
export function chimeKey(a: OpsAlert): string {
  return `${a.id}:${a.state}`;
}

/**
 * Стоит ли звонить по этой карточке.
 *
 * По info НЕ звоним, и это не мелочь: «биржа сообщила ставку комиссии» и
 * «оператор взвёл стоп» — новости, а звонок на новость обесценивает звонок на
 * поломку. Снятие тревоги звонит: оператор, услышавший тревогу, обязан
 * услышать и отбой, иначе пойдёт проверять руками.
 */
export function shouldChime(a: OpsAlert): boolean {
  if (a.state === STATE_MUTED) return false;
  if (a.severity === "info") return false;
  return true;
}

/**
 * Какие карточки ещё не прозвучали.
 *
 * Возвращает и список к звонку, и обновлённое множество прозвучавших. Чистой
 * функцией, чтобы это можно было проверить тестом: побочный эффект в хуке
 * проверять нечем, а забытая пометка означала бы звонок на каждый рендер.
 */
export function unannounced(
  alerts: OpsAlert[],
  announced: ReadonlySet<string>,
): { ring: OpsAlert[]; next: Set<string> } {
  const next = new Set(announced);
  const ring: OpsAlert[] = [];
  for (const a of alerts) {
    const key = chimeKey(a);
    if (next.has(key)) continue;
    next.add(key);
    // Пометка ставится ВСЕГДА, даже когда звонить не надо: иначе выключенный
    // звук копил бы «непрозвучавшие» карточки, и включение звука превратилось
    // бы в залп по всему, что случилось за смену. То же правило, что у замка.
    if (shouldChime(a)) ring.push(a);
  }
  return { ring, next };
}

/**
 * Вправе ли пришедшая по REST карточка заменить уже показанную.
 *
 * Догон и сокет несут одни и те же события, но REST-ответ мог пролежать в
 * сети секунды: без сверки времени задержанный ответ откатывал бы уже снятую
 * по сокету тревогу обратно в «тревога» до следующего опроса. Равное время
 * оставляет показанную — это тот же снимок, а не новость. Нечитаемое время у
 * пришедшей тоже оставляет показанную: заменять знакомое на неразборчивое
 * нельзя, а вот пустое место заполнить можно.
 */
export function fresher(cur: OpsAlert | undefined, incoming: OpsAlert): boolean {
  if (!cur) return true;
  const a = Date.parse(incoming.at ?? "");
  const b = Date.parse(cur.at ?? "");
  if (Number.isNaN(a)) return false;
  if (Number.isNaN(b)) return true;
  return a > b;
}

/**
 * Порядок показа: сначала живые, внутри — по тяжести, затем свежие сверху.
 *
 * Живые выше снятых безусловно: снятая тревога это история, а живая требует
 * действия, и мешать их по времени значило бы прятать crit под свежим отбоем.
 */
export function sortAlerts(alerts: OpsAlert[]): OpsAlert[] {
  return [...alerts].sort((a, b) => {
    if (isLive(a) !== isLive(b)) return isLive(a) ? -1 : 1;
    const w = severityWeight(b.severity) - severityWeight(a.severity);
    if (w !== 0) return w;
    return Date.parse(b.at ?? "") - Date.parse(a.at ?? "");
  });
}

/**
 * Пора ли убрать карточку с экрана.
 *
 * Гаснут только СНЯТЫЕ: живая тревога висит, пока держится, сколько бы это ни
 * длилось. Убрать живую по таймеру значило бы соврать, что всё прошло.
 */
export function isExpired(a: OpsAlert, ttlSec: number, now: number): boolean {
  if (isLive(a)) return false;
  if (!ttlSec || ttlSec <= 0) return false;
  const at = Date.parse(a.at ?? "");
  if (Number.isNaN(at)) return false;
  return now - at > ttlSec * 1000;
}

/** Заголовок карточки: правило и, если оно шаблонное, экземпляр. */
export function title(a: OpsAlert): string {
  return a.instance ? `${a.rule_id} · ${a.instance}` : a.rule_id;
}

/**
 * Подпись состояния для человека.
 *
 * У снятия обязана быть ДЛИТЕЛЬНОСТЬ: «полторы минуты» и «шесть часов» — разные
 * новости об одном и том же событии, и без неё отбой не говорит ничего.
 */
export function stateLabel(a: OpsAlert): string {
  if (a.state !== STATE_RESOLVED) return a.state;
  const s = a.duration_s ?? 0;
  if (s <= 0) return "снято";
  return `снято, держалось ${humanDuration(s)}`;
}

export function humanDuration(sec: number): string {
  if (sec < 60) return `${Math.round(sec)} с`;
  if (sec < 3600) return `${Math.round(sec / 60)} мин`;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
}

/**
 * Что показать вместо списка.
 *
 * Три состояния, а не два, и это то же различие, ради которого ответ несёт
 * known: «запрос не прошёл», «тревоги выключены» и «тревог нет» выглядят на
 * экране одинаковой пустотой, а означают разное. Первое чинят, второе —
 * решение оператора, третье — то, чего и ждут.
 */
export function emptyReason(known: boolean, failed: boolean): string | null {
  if (failed) return "состояние тревог не читается: возможно, web_api не отвечает";
  if (!known) return "операционные тревоги выключены (OPS_ENABLED)";
  return null;
}
